import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useProductService } from 'services/products';
import { useApiClient } from 'hooks/useApiClient';
import { CreateProductRequest } from 'types/product';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { ArrowLeft, Upload, X } from 'lucide-react';
import styles from './AddProduct.module.scss';

type ProductFormData = {
  name: string;
  description: string;
  productCategory: string;
  price: number;
  productType: 'product' | 'service';
  isShippable: boolean;
  weight: number;
  weightUnit: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  shippingClass: string;
};

function AddProduct() {
  const navigate = useNavigate();
  const productService = useProductService();
  const apiClient = useApiClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      name: '',
      description: '',
      productCategory: '',
      price: 0,
      productType: 'product',
      isShippable: false,
      weight: 0,
      weightUnit: 'kg',
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit: 'cm',
      shippingClass: '',
    },
  });

  const productType = watch('productType');
  const isService = productType === 'service';
  const isShippable = watch('isShippable');

  // Update showShippingDetails when isShippable changes
  useEffect(() => {
    setShowShippingDetails(isShippable);
  }, [isShippable]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const handleMediaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const newFiles = [...mediaFiles, ...files];
      setMediaFiles(newFiles);

      // Create previews
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setMediaPreviews([...mediaPreviews, ...newPreviews]);
    },
    [mediaFiles, mediaPreviews],
  );

  const handleRemoveMedia = useCallback(
    (index: number) => {
      const newFiles = mediaFiles.filter((_, i) => i !== index);
      const newPreviews = mediaPreviews.filter((_, i) => i !== index);
      setMediaFiles(newFiles);
      setMediaPreviews(newPreviews);
      // Revoke object URLs to prevent memory leaks
      URL.revokeObjectURL(mediaPreviews[index]);
    },
    [mediaFiles, mediaPreviews],
  );

  const buildProductData = useCallback(
    (data: ProductFormData): CreateProductRequest => {
      const mediaUrls = mediaFiles.map((file) => file.name);
      const shouldTrackInventory = data.productType === 'product';

      return {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        productCategory: data.productCategory.trim() || undefined,
        price: Number(data.price),
        trackInventory: shouldTrackInventory,
        isShippable: data.isShippable,
        shippingDetails: data.isShippable
          ? {
              weight: data.weight > 0 ? Number(data.weight) : undefined,
              weightUnit: data.weightUnit || undefined,
              length: data.length > 0 ? Number(data.length) : undefined,
              width: data.width > 0 ? Number(data.width) : undefined,
              height: data.height > 0 ? Number(data.height) : undefined,
              dimensionUnit: data.dimensionUnit || undefined,
              shippingClass: data.shippingClass.trim() || undefined,
            }
          : undefined,
        media: mediaUrls.length > 0 ? mediaUrls : undefined,
      };
    },
    [mediaFiles],
  );

  const onSubmit: SubmitHandler<ProductFormData> = async (data) => {
    if (!apiClient.companyId) {
      showToast(
        'Company ID is required. Please ensure you are part of a company.',
        'error',
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const productData = buildProductData(data);
      await productService.createProduct(productData);
      showToast('Product created successfully!', 'success');
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.ROOT);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create product. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSaveDraft: SubmitHandler<ProductFormData> = async (data) => {
    if (!apiClient.companyId) {
      showToast(
        'Company ID is required. Please ensure you are part of a company.',
        'error',
      );
      return;
    }

    if (!data.name.trim()) {
      showToast('Product name is required to save as draft.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const productData = buildProductData(data);
      await productService.saveDraftProduct(productData);
      showToast('Product saved as draft!', 'success');
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.ROOT);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to save draft. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <button
          onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
          className={styles.backButton}
          aria-label="Back to products"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className={styles.title}>Create Product</h1>
      </div>

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        className={styles.form}
      >
        {/* Basic Information */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>

          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.label}>
              Product Name <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              type="text"
              className={`${styles.textInput} ${
                errors.name ? styles.errorInput : ''
              }`}
              {...register('name', {
                required: 'Product name is required',
                minLength: {
                  value: 1,
                  message: 'Product name must be at least 1 character',
                },
              })}
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className={styles.errorMessage}>{errors.name.message}</p>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>
              Description
            </label>
            <textarea
              id="description"
              className={styles.textarea}
              {...register('description')}
              placeholder="Enter product description"
              rows={4}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="productCategory" className={styles.label}>
              Product Category
            </label>
            <input
              id="productCategory"
              type="text"
              className={styles.textInput}
              {...register('productCategory')}
              placeholder="Enter category (optional)"
            />
            <p className={styles.helperText}>
              Group products by category for better organization
            </p>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="price" className={styles.label}>
              Price <span className={styles.required}>*</span>
            </label>
            <input
              id="price"
              type="number"
              step="0.01"
              min="0"
              className={`${styles.numberInput} ${
                errors.price ? styles.errorInput : ''
              }`}
              {...register('price', {
                required: 'Price is required',
                min: {
                  value: 0,
                  message: 'Price must be greater than or equal to 0',
                },
              })}
              placeholder="0.00"
            />
            {errors.price && (
              <p className={styles.errorMessage}>{errors.price.message}</p>
            )}
          </div>
        </div>

        {/* Media */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Media</h2>
          <div className={styles.formGroup}>
            <label htmlFor="media" className={styles.label}>
              Product Images
            </label>
            <div className={styles.mediaUpload}>
              <input
                id="media"
                type="file"
                accept="image/*"
                multiple
                onChange={handleMediaChange}
                className={styles.fileInput}
              />
              <label htmlFor="media" className={styles.uploadButton}>
                <Upload size={18} />
                Upload Images
              </label>
            </div>
            {mediaPreviews.length > 0 && (
              <div className={styles.mediaPreviews}>
                {mediaPreviews.map((preview, index) => (
                  <div key={index} className={styles.mediaPreview}>
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => handleRemoveMedia(index)}
                      className={styles.removeMediaButton}
                      aria-label="Remove image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Product Type */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Product Type</h2>
          <div className={styles.formGroup}>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="product"
                  {...register('productType')}
                  className={styles.radio}
                />
                <span>Product</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="service"
                  {...register('productType')}
                  className={styles.radio}
                />
                <span>Service</span>
              </label>
            </div>
            <p className={styles.helperText}>
              Products track inventory, while services don't. This setting
              cannot be changed after creation.
            </p>
          </div>
        </div>

        {/* Shipping */}
        {!isService && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Shipping</h2>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  {...register('isShippable')}
                  className={styles.checkbox}
                />
                <span>This product requires shipping</span>
              </label>
            </div>

            {showShippingDetails && (
              <div className={styles.shippingDetails}>
                <div className={styles.formGroup}>
                  <label htmlFor="weight" className={styles.label}>
                    Weight
                  </label>
                  <div className={styles.weightInputGroup}>
                    <input
                      id="weight"
                      type="number"
                      step="0.01"
                      min="0"
                      className={styles.numberInput}
                      {...register('weight', {
                        min: {
                          value: 0,
                          message: 'Weight must be greater than or equal to 0',
                        },
                      })}
                      placeholder="0.00"
                    />
                    <select
                      {...register('weightUnit')}
                      className={styles.unitSelect}
                    >
                      <option value="kg">kg</option>
                      <option value="lb">lb</option>
                      <option value="g">g</option>
                      <option value="oz">oz</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Dimensions</label>
                  <div className={styles.dimensionsGrid}>
                    <div className={styles.dimensionField}>
                      <label htmlFor="length" className={styles.dimensionLabel}>
                        Length
                      </label>
                      <input
                        id="length"
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('length', {
                          min: {
                            value: 0,
                            message:
                              'Length must be greater than or equal to 0',
                          },
                        })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className={styles.dimensionField}>
                      <label htmlFor="width" className={styles.dimensionLabel}>
                        Width
                      </label>
                      <input
                        id="width"
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('width', {
                          min: {
                            value: 0,
                            message: 'Width must be greater than or equal to 0',
                          },
                        })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className={styles.dimensionField}>
                      <label htmlFor="height" className={styles.dimensionLabel}>
                        Height
                      </label>
                      <input
                        id="height"
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('height', {
                          min: {
                            value: 0,
                            message:
                              'Height must be greater than or equal to 0',
                          },
                        })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <select
                    {...register('dimensionUnit')}
                    className={styles.unitSelect}
                    style={{ marginTop: '0.5rem' }}
                  >
                    <option value="cm">cm</option>
                    <option value="in">in</option>
                    <option value="m">m</option>
                    <option value="ft">ft</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="shippingClass" className={styles.label}>
                    Shipping Class
                  </label>
                  <input
                    id="shippingClass"
                    type="text"
                    className={styles.textInput}
                    {...register('shippingClass')}
                    placeholder="e.g., Standard, Express, Fragile"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              void handleSubmit(onSaveDraft)(e);
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Product'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AddProduct;
